import { useLayoutEffect } from 'react';
import { useMatches } from 'react-router-dom';

/** The attribute on `<html>` that `theme-revamp.css` keys its re-pointed colour names on. */
export const THEME_ATTRIBUTE = 'data-et-theme';

function themeOf(handle: unknown): string | undefined {
    if (
        typeof handle === 'object' &&
        handle !== null &&
        'theme' in handle &&
        typeof handle.theme === 'string'
    ) {
        return handle.theme;
    }
    return undefined;
}

/**
 * Revamp (AD #39): the theme the matched route names in `handle.theme`, read the way `RouteTitle` reads
 * `handle.title` — the deepest match that names one wins — or `undefined` when none does. `RouteTheme`
 * below applies it; the frame itself is the same on every route.
 */
export function useRouteTheme(): string | undefined {
    const matches = useMatches();
    let theme: string | undefined;
    for (const match of matches) {
        theme = themeOf(match.handle) ?? theme;
    }
    return theme;
}

/**
 * Revamp (AD #39): applies a route's colour theme to the whole document. A route opts in with
 * `handle: { theme: 'revamp' }` in the route table; a route that names none leaves `<html>` without
 * the attribute, so every other page keeps the Empire theme.
 *
 * On `<html>` rather than on the page's own wrapper because the theme has to reach what renders
 * outside the route's subtree: the sidebar, and the Combobox and Tooltip lists, which the library
 * portals to `document.body` when no modal is open.
 *
 * A layout effect, so the attribute changes before the browser paints the navigation; and rendered
 * outside the layout's Suspense boundary, so it is not held back while a lazy route chunk loads.
 */
export function RouteTheme() {
    const theme = useRouteTheme();
    useLayoutEffect(() => {
        const root = document.documentElement;
        if (theme === undefined) {
            root.removeAttribute(THEME_ATTRIBUTE);
        } else {
            root.setAttribute(THEME_ATTRIBUTE, theme);
        }
    }, [theme]);
    useLayoutEffect(() => () => document.documentElement.removeAttribute(THEME_ATTRIBUTE), []);
    return null;
}
